// AudioProvider: a teacher's recording when one exists (uploaded in the admin, stored in the site's storage),
// otherwise the voice the learner chose in the preferences: Bhashini (Premium, @/lib/client/pronunciation) or the
// device's own voice (the browser's speech synthesis, a Hindi or other Devanagari voice), which falls in whenever
// Bhashini cannot play. Both are approximations of Prakrit pronunciation and can be switched off in the settings.
// It also hands the voice choice to the Sanskrit course's audio layer (configureVoice).

import type { ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { usePrefs } from '@/lib/client/prefs'
import { useSession } from '@/lib/client/session'
import { configureVoice, speakBhashini, stopBhashini } from '@/lib/client/pronunciation'

export type Speed = 'normal' | 'slow'

export type AudioSource = 'recording' | 'voice'

export interface AudioEntry {
    normal?: string
    slow?: string
}

/** The storage key for a Prakrit word or sentence: NFC, punctuation removed, single spaces. */
export const audioKey = (text: string): string => 'pra|' + text
    .normalize('NFC')
    .replace(/[।॥?!,;:.“”"'‘’()[\]{}…—–-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ')

const VOICE_PREF_KEY = 'vaani:voice'

/** Devanagari voices in order of preference: Hindi reads Prakrit most naturally, then Marathi, Nepali, Sanskrit. */
const VOICE_LANGS = ['hi', 'mr', 'ne', 'sa']

const pickVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null => {
    const lang = (v: SpeechSynthesisVoice) => v.lang.replace('_', '-').toLowerCase()

    for (const want of VOICE_LANGS) {
        const matches = voices.filter(v => lang(v) === want || lang(v).startsWith(`${want}-`))

        if (matches.length > 0) return matches.find(v => v.localService) ?? matches[0]
    }

    return null
}

const textOfKey = (key: string): string => (key.startsWith('pra|') ? key.slice(4) : key)

interface AudioContextValue {
    ready: boolean
    /** Something can be played for this key: a recording, or the device voice. */
    has: (key: string) => boolean
    /** Where the sound for this key comes from, or null when nothing can play it. */
    source: (key: string) => AudioSource | null
    play: (key: string, speed?: Speed) => Promise<boolean>
    /** Read any Prakrit text aloud with the device voice (for listening practice built without recordings). */
    speak: (text: string, speed?: Speed) => Promise<boolean>
    stop: () => void
    playing: string | null
    /** The browser has a Devanagari voice. */
    voiceSupported: boolean
    /** The learner allows reading aloud where there is no recording (on by default). */
    voiceEnabled: boolean
    /** The device's Devanagari voices (Hindi, Marathi, Nepali, Sanskrit), for the voice picker. */
    deviceVoices: SpeechSynthesisVoice[]
    /** Bhashini reads aloud (chosen in the preferences, and Premium). */
    bhashini: boolean
    setVoiceEnabled: (on: boolean) => void
}

const AudioCtx = createContext<AudioContextValue | null>(null)

export const AudioProvider = ({ children }: { children: ReactNode }): ReactNode => {
    const [manifest, setManifest] = useState<Record<string, AudioEntry>>({})
    const [ready, setReady] = useState(false)
    const [playing, setPlaying] = useState<string | null>(null)
    const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
    const [voiceEnabled, setVoiceEnabledState] = useState(true)
    const current = useRef<HTMLAudioElement | null>(null)
    const { voice: engine, voiceGender, speechRate, deviceVoice } = usePrefs()
    const { access } = useSession()
    const premium = access.prakrit || access.sanskrit
    const bhashini = engine === 'bhashini' && premium
    const voice = useMemo(() => (deviceVoice ? voices.find(v => v.voiceURI === deviceVoice) : undefined) ?? pickVoice(voices), [voices, deviceVoice])
    const deviceVoices = useMemo(() => voices.filter(v => VOICE_LANGS.some(l => v.lang.replace('_', '-').toLowerCase().startsWith(l))), [voices])

    useEffect(() => {
        configureVoice({ engine, gender: voiceGender, premium, rate: speechRate })
    }, [engine, voiceGender, premium, speechRate])

    useEffect(() => {
        let cancelled = false

        fetch('/api/audio/manifest')
            .then(res => (res.ok ? res.json() : { items: {} }))
            .then((data: { items?: Record<string, AudioEntry> }) => {
                if (!cancelled) setManifest(data.items ?? {})
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setReady(true)
            })

        return () => {
            cancelled = true
        }
    }, [])

    // The device voice: read the saved choice, then find a Devanagari voice (the list often arrives late).
    useEffect(() => {
        try {
            if (localStorage.getItem(VOICE_PREF_KEY) === 'off') setVoiceEnabledState(false)
        }
        catch {
            // Unreadable storage keeps the voice on.
        }

        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return

        const synth = window.speechSynthesis
        const load = () => setVoices(synth.getVoices())

        load()
        synth.addEventListener('voiceschanged', load)

        return () => {
            synth.removeEventListener('voiceschanged', load)
            synth.cancel()
        }
    }, [])

    const setVoiceEnabled = useCallback((on: boolean) => {
        setVoiceEnabledState(on)

        try {
            localStorage.setItem(VOICE_PREF_KEY, on ? 'on' : 'off')
        }
        catch {
            // Private mode: the choice applies for this visit.
        }

        if (!on && 'speechSynthesis' in window) window.speechSynthesis.cancel()
    }, [])

    const stop = useCallback(() => {
        stopBhashini()
        current.current?.pause()
        current.current = null

        if (typeof window !== 'undefined' && 'speechSynthesis' in window) window.speechSynthesis.cancel()

        setPlaying(null)
    }, [])

    const recorded = useCallback((key: string) => Boolean(manifest[key]?.normal || manifest[key]?.slow), [manifest])

    const canSpeak = voiceEnabled && (voice !== null || bhashini)

    const source = useCallback((key: string): AudioSource | null => {
        if (recorded(key)) return 'recording'

        return canSpeak && textOfKey(key).length > 0 ? 'voice' : null
    }, [recorded, canSpeak])

    const has = useCallback((key: string) => source(key) !== null, [source])

    const say = useCallback(async (text: string, speed: Speed, tag: string): Promise<boolean> => {
        if (!voiceEnabled || !text.trim()) return false

        const rate = speed === 'slow' ? Math.max(0.45, speechRate * 0.65) : speechRate
        const done = () => setPlaying(p => (p === tag ? null : p))

        if (bhashini) {
            stop()
            setPlaying(tag)

            // Resolves as soon as Bhashini starts playing, or false (then the device voice reads it) when it cannot.
            const started = await new Promise<boolean>(resolve => {
                void speakBhashini(text, { lang: 'hi', gender: voiceGender, rate, onStart: () => resolve(true), onEnd: done })
                    .then(ok => { if (!ok) resolve(false) })
            })

            if (started) return true

            setPlaying(null)
        }

        if (!voice) return false

        stop()

        const utterance = new SpeechSynthesisUtterance(text)

        utterance.voice = voice
        utterance.lang = voice.lang
        utterance.rate = rate

        utterance.addEventListener('end', done)
        utterance.addEventListener('error', done)
        setPlaying(tag)
        window.speechSynthesis.speak(utterance)

        return true
    }, [voice, voiceEnabled, stop, bhashini, speechRate, voiceGender])

    const play = useCallback(async (key: string, speed: Speed = 'normal') => {
        const entry = manifest[key]
        const url = speed === 'slow' ? entry?.slow ?? entry?.normal : entry?.normal ?? entry?.slow

        if (!url) return say(textOfKey(key), speed, key)

        stop()

        const audio = new Audio(url)

        if (speed === 'slow' && !entry?.slow) {
            audio.playbackRate = 0.7
            audio.preservesPitch = true
        }

        current.current = audio
        setPlaying(key)
        audio.addEventListener('ended', () => setPlaying(p => (p === key ? null : p)))

        try {
            await audio.play()

            return true
        }
        catch {
            setPlaying(null)

            return false
        }
    }, [manifest, stop, say])

    const speak = useCallback((text: string, speed: Speed = 'normal') => say(text, speed, audioKey(text)), [say])

    const value = useMemo(() => ({
        ready,
        has,
        source,
        play,
        speak,
        stop,
        playing,
        voiceSupported: voice !== null,
        voiceEnabled,
        setVoiceEnabled,
        deviceVoices,
        bhashini
    }), [ready, has, source, play, speak, stop, playing, voice, voiceEnabled, setVoiceEnabled, deviceVoices, bhashini])

    return <AudioCtx.Provider value={value}>{children}</AudioCtx.Provider>
}

export const useAudio = (): AudioContextValue => {
    const ctx = useContext(AudioCtx)

    if (!ctx) throw new Error('useAudio must be used inside AudioProvider')

    return ctx
}
